import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { logger } from '@/services/logger'
import {
  getEmployeesByCompanyId,
  getInvitationsByCompanyId,
  patchEmployee,
  postInvitations,
  deleteInvitation,
  type EmployeeType,
  type InvitationType
} from '@/services/employees'
import { useEmployeeStore } from '@/stores/employee'
import { isBeforeDate } from '@/helpers/date-helpers'
import { COLLABORATOR_STATUS } from '@/constants/collaborator'
import type { DeepPartial } from '@/helpers/utils'

export const useCompanyEmployeesStore = defineStore('company-employees', () => {
  const companyEmployees = ref<EmployeeType[]>([])
  const companyInvitations = ref<InvitationType[]>([])
  const getEmployeesPendingPromise = ref<Promise<void> | null>(null)

  const getEmployees = async () => {
    if (getEmployeesPendingPromise.value) {
      await getEmployeesPendingPromise.value

      return
    }

    const promise = executeGetEmployees()
    getEmployeesPendingPromise.value = promise
    await promise
  }

  const executeGetEmployees = async () => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      const response = await getEmployeesByCompanyId(employeeStore.employee.companyId)

      if (!response.data || response.status !== 200) {
        throw new Error('Failed to get employees by company ID')
      }

      companyEmployees.value = response.data.employees
    } catch (error) {
      logger.error(error)

      throw error
    } finally {
      getEmployeesPendingPromise.value = null
    }
  }

  const getLastAddedEmployees = async () => {
    if (companyEmployees.value.length === 0) {
      await getEmployees()
    }

    return companyEmployees.value
      .filter((employee) => employee.tracking?.createdDate)
      .sort((a, b) => {
        const dateA = a.tracking?.createdDate
        const dateB = b.tracking?.createdDate
        if (!dateA || !dateB) return 0

        return isBeforeDate(dateA, dateB) ? 1 : -1
      })
      .slice(0, 3)
  }

  const administrators = computed(() => {
    return companyEmployees.value.filter(
      (employee) => employee.authorization.role === COLLABORATOR_STATUS.ADMINISTRATOR
    )
  })

  const employees = computed(() => {
    return companyEmployees.value.filter(
      (employee) => employee.authorization.role === COLLABORATOR_STATUS.EMPLOYEE
    )
  })

  const updateEmployee = async (employeeId: string, employee: DeepPartial<EmployeeType>) => {
    try {
      const employeeStore = useEmployeeStore()

      if (!employeeStore.employee) throw new Error('Employee not found')
      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      const response = await patchEmployee(employeeStore.employee.companyId, employeeId, employee)

      if (!response.data || response.status !== 200) throw new Error('Failed to update employee')

      companyEmployees.value = companyEmployees.value.map((employee: EmployeeType) => {
        if (employee.pmid === response.data.pmid) {
          return { ...employee, ...response.data }
        }

        return employee
      })
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  const getInvitations = async () => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      const response = await getInvitationsByCompanyId(employeeStore.employee.companyId)

      if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to get invitations by company ID')
      }

      companyInvitations.value = response.data?.invitations ?? []
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  const inviteEmployee = async (
    email: string,
    role: COLLABORATOR_STATUS
  ): Promise<{ success?: boolean; errorCode?: string }> => {
    try {
      const employeeStore = useEmployeeStore()

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      const response = await postInvitations(employeeStore.employee.companyId, {
        invitees: [
          {
            email,
            role
          }
        ]
      })

      const errorItem = response.data?.errors?.[0]
      if (errorItem) {
        return { errorCode: errorItem.code }
      }

      if (response.data?.success?.[0]) {
        await getInvitations()

        return { success: true }
      }

      return { errorCode: 'UNKNOWN' }
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  const deleteCompanyInvitation = async (invitationId: string) => {
    try {
      const employeeStore = useEmployeeStore()

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      await deleteInvitation(employeeStore.employee.companyId, invitationId)

      companyInvitations.value = companyInvitations.value.filter(
        (invitation) => invitation.id !== invitationId
      )
    } catch (error) {
      logger.error(error)
      throw error
    }
  }

  const employeesCountByDomainName = (domainNames: readonly string[]) => {
    const domains = domainNames.map((domain) => domain.trim()).filter(Boolean)
    if (domains.length === 0) return 0

    const matchesDomain = (email: string) => domains.some((domain) => email.endsWith(domain))

    const employeeCount = companyEmployees.value.filter((e) =>
      matchesDomain(e.professionalEmail)
    ).length
    const invitationCount = companyInvitations.value.filter((inv) =>
      matchesDomain(inv.invitee.email)
    ).length

    return employeeCount + invitationCount
  }

  return {
    administrators,
    companyEmployees,
    companyInvitations,
    employees,
    employeesCountByDomainName,
    getEmployees,
    getInvitations,
    deleteCompanyInvitation,
    getLastAddedEmployees,
    inviteEmployee,
    updateEmployee
  }
})
